import React, { useCallback, useRef, useState } from 'react';
import type {
  CoopAnagramBoard,
  CoopChainBoard,
  CoopDrawGuessBoard,
  CoopPayload,
  CoopRuntime,
} from '../../core/types/payloads/coop.types';

function CoopShell({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-2xl p-5 sm:p-8"
      style={{ background: 'var(--plugin-bg)', border: '1px solid var(--plugin-border)', color: 'var(--plugin-text)' }}
    >
      {badge && (
        <span className="mb-3 inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest" style={{ background: 'var(--plugin-btn-bg)', color: '#14b8a6' }}>
          {badge}
        </span>
      )}
      <h3 className="font-display text-xl font-bold sm:text-2xl">{title}</h3>
      <div className="mt-6">{children}</div>
    </div>
  );
}

export function ChainStage({
  payload,
  runtime,
  canAct,
  dispatch,
  size = 'md',
}: {
  payload: CoopPayload & { board: CoopChainBoard };
  runtime: CoopRuntime;
  canAct?: boolean;
  dispatch?: (action: { type: string; payload?: unknown }) => void;
  size?: 'md' | 'lg';
}) {
  const board = payload.board;
  const fills = runtime.chainFills ?? {};
  const idx = runtime.currentSegmentIndex ?? 0;
  const segment = board.segments[idx];
  const textSize = size === 'lg' ? 'text-lg sm:text-xl' : 'text-base';

  return (
    <CoopShell title={payload.title} badge={runtime.won ? 'Texto completo' : `Conector ${idx + 1} de ${board.segments.length}`}>
      {board.intro && <p className="mb-4 text-sm" style={{ color: 'var(--plugin-muted)' }}>{board.intro}</p>}
      <div className={`coop-chain-text leading-relaxed ${textSize}`}>
        {board.segments.map((seg, i) => (
          <span key={seg.id}>
            {seg.textBefore}{' '}
            <span
              className="coop-chain-blank mx-1 inline-block min-w-[4rem] rounded px-2 py-0.5 font-bold"
              style={{
                background: fills[seg.id] ? '#22c55e22' : '#f59e0b22',
                color: fills[seg.id] ? '#15803d' : '#b45309',
                borderBottom: fills[seg.id] ? '2px solid #22c55e' : '2px dashed #f59e0b',
              }}
            >
              {fills[seg.id] ?? '…'}
            </span>{' '}
            {seg.textAfter}{' '}
          </span>
        ))}
      </div>
      {canAct && segment && dispatch && !runtime.won && (
        <div className="mt-6 grid gap-2 sm:grid-cols-2">
          {segment.choices.map((choice) => (
            <button
              key={choice}
              type="button"
              className="rounded-xl border-2 px-4 py-3 text-sm font-bold transition active:scale-[0.98]"
              style={{ borderColor: 'var(--plugin-border)', background: 'var(--plugin-btn-bg)' }}
              onClick={() => dispatch({ type: 'PICK_CONNECTOR', payload: { connector: choice } })}
            >
              {choice}
            </button>
          ))}
        </div>
      )}
    </CoopShell>
  );
}

export function AnagramStage({
  payload,
  runtime,
  canAct,
  dispatch,
}: {
  payload: CoopPayload & { board: CoopAnagramBoard };
  runtime: CoopRuntime;
  canAct?: boolean;
  dispatch?: (action: { type: string; payload?: unknown }) => void;
}) {
  const board = payload.board;
  const used = new Set(runtime.usedLetterIndices ?? []);

  return (
    <CoopShell title={payload.title} badge={runtime.won ? '¡Palabra encontrada!' : 'Fábrica de anagramas'}>
      <p className="text-sm" style={{ color: 'var(--plugin-muted)' }}>{board.hint}</p>
      <div
        className="mt-4 flex min-h-[3rem] flex-wrap items-center gap-2 rounded-xl border-2 border-dashed px-4 py-3 font-display text-2xl font-extrabold tracking-widest"
        style={{ borderColor: 'var(--plugin-border)' }}
      >
        {runtime.builtWord || <span className="text-sm font-normal opacity-40">Arma la palabra…</span>}
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {board.letterPool.map((letter, i) => (
          <button
            key={`${letter}-${i}`}
            type="button"
            disabled={!canAct || !dispatch || used.has(i)}
            className="flex h-11 w-11 items-center justify-center rounded-lg font-display text-lg font-bold disabled:opacity-30"
            style={{ background: used.has(i) ? 'var(--plugin-border)' : '#6366f1', color: used.has(i) ? 'var(--plugin-muted)' : '#fff' }}
            onClick={() => dispatch?.({ type: 'ADD_LETTER', payload: { letterIndex: i } })}
          >
            {letter}
          </button>
        ))}
      </div>
      {canAct && dispatch && !runtime.won && (
        <div className="mt-4 flex gap-2">
          <button type="button" className="flex-1 rounded-xl py-3 text-sm font-bold" style={{ background: 'var(--plugin-btn-bg)' }} onClick={() => dispatch({ type: 'UNDO_LETTER', payload: {} })}>
            Borrar
          </button>
          <button type="button" className="flex-1 rounded-xl py-3 text-sm font-bold text-white" style={{ background: '#22c55e' }} onClick={() => dispatch({ type: 'SUBMIT_WORD', payload: {} })}>
            Enviar palabra
          </button>
        </div>
      )}
    </CoopShell>
  );
}

function strokesToPath(strokes: CoopRuntime['strokes']) {
  return (strokes ?? [])
    .map((s) => {
      if (s.points.length < 2) return '';
      const [first, ...rest] = s.points;
      return `M ${first.x * 100} ${first.y * 100} ${rest.map((p) => `L ${p.x * 100} ${p.y * 100}`).join(' ')}`;
    })
    .join(' ');
}

export function DrawGuessStage({
  payload,
  runtime,
  participantId,
  canAct,
  dispatch,
  size = 'md',
}: {
  payload: CoopPayload & { board: CoopDrawGuessBoard };
  runtime: CoopRuntime;
  participantId?: string;
  canAct?: boolean;
  dispatch?: (action: { type: string; payload?: unknown }) => void;
  size?: 'md' | 'lg';
}) {
  const board = payload.board;
  const isDrawer = participantId && participantId === runtime.drawerId;
  const canvasH = size === 'lg' ? 320 : 220;
  const drawingRef = useRef(false);
  const pointsRef = useRef<Array<{ x: number; y: number }>>([]);
  const [guess, setGuess] = useState('');
  const guesses = Object.values(runtime.guesses ?? {});

  const normPoint = useCallback((clientX: number, clientY: number, el: HTMLDivElement) => {
    const rect = el.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  }, []);

  const endStroke = useCallback(() => {
    if (!dispatch || pointsRef.current.length < 2) return;
    dispatch({ type: 'STROKE', payload: { points: [...pointsRef.current] } });
    pointsRef.current = [];
    drawingRef.current = false;
  }, [dispatch]);

  return (
    <CoopShell
      title={payload.title}
      badge={board.sketchMode === 'melody' ? 'Lienzo del sonido' : 'Pictionary conceptual'}
    >
      <p className="text-sm" style={{ color: 'var(--plugin-muted)' }}>{board.prompt}</p>
      <div
        className="coop-draw-canvas relative mt-4 overflow-hidden rounded-xl border-2"
        style={{ borderColor: 'var(--plugin-border)', height: canvasH, background: board.sketchMode === 'melody' ? '#f0f9ff' : '#fffef8', touchAction: 'none' }}
        onPointerDown={(e) => {
          if (!isDrawer || !canAct) return;
          drawingRef.current = true;
          pointsRef.current = [normPoint(e.clientX, e.clientY, e.currentTarget)];
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!drawingRef.current || !isDrawer) return;
          pointsRef.current.push(normPoint(e.clientX, e.clientY, e.currentTarget));
        }}
        onPointerUp={endStroke}
        onPointerLeave={endStroke}
      >
        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d={strokesToPath(runtime.strokes)} fill="none" stroke="#1e293b" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
          {drawingRef.current && pointsRef.current.length > 1 && (
            <path
              d={`M ${pointsRef.current[0].x * 100} ${pointsRef.current[0].y * 100} ${pointsRef.current.slice(1).map((p) => `L ${p.x * 100} ${p.y * 100}`).join(' ')}`}
              fill="none"
              stroke="#6366f1"
              strokeWidth="0.8"
              strokeLinecap="round"
            />
          )}
        </svg>
        {board.sketchMode === 'melody' && (
          <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px bg-sky-300/60" />
        )}
        {!isDrawer && canAct && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs font-bold uppercase tracking-widest opacity-30">
            Observa el dibujo
          </p>
        )}
      </div>
      {!isDrawer && canAct && dispatch && (
        <div className="mt-4 flex gap-2">
          <input
            className="flex-1 rounded-xl border-2 px-3 py-2 text-sm"
            style={{ borderColor: 'var(--plugin-border)', background: 'var(--plugin-btn-bg)' }}
            placeholder="Escribe tu conjetura…"
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            maxLength={80}
          />
          <button
            type="button"
            className="rounded-xl px-4 py-2 text-sm font-bold text-white"
            style={{ background: '#6366f1' }}
            onClick={() => {
              if (!guess.trim()) return;
              dispatch({ type: 'SUBMIT_GUESS', payload: { text: guess.trim() } });
              setGuess('');
            }}
          >
            Enviar
          </button>
        </div>
      )}
      {guesses.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {guesses.map((g, i) => (
            <span key={`${g}-${i}`} className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: '#6366f122', color: '#4f46e5' }}>
              {g}
            </span>
          ))}
        </div>
      )}
    </CoopShell>
  );
}
